import { db } from '@/lib/firebase';
import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  query,
  orderBy,
  onSnapshot,
  Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TrackComment {
  id: string;
  trackId: string;
  userId: string;
  userName: string;
  userImageUrl?: string;
  content: string;
  /** Playback position in seconds where this comment is pinned (0 = global/no position) */
  timestampSeconds: number;
  /** true = general comment not tied to a specific time */
  isGlobal: boolean;
  createdAt: Timestamp;
  /** Emoji reactions: emoji → array of userIds who reacted */
  reactions?: Record<string, string[]>;
}

/** Supported reaction emojis */
export const COMMENT_REACTIONS = ['👍', '❤️', '😂', '🔥', '😮'] as const;
export type CommentReactionEmoji = (typeof COMMENT_REACTIONS)[number];

export interface CreateCommentInput {
  trackId: string;
  userId: string;
  userName: string;
  userImageUrl?: string;
  content: string;
  timestampSeconds?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format seconds as mm:ss */
export function formatCommentTimestamp(seconds: number): string {
  if (!seconds || seconds <= 0) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

/**
 * Add a comment to a track.
 * Comments are stored in `trackComments/{trackId}/comments/{commentId}`.
 */
export async function addTrackComment(input: CreateCommentInput): Promise<string> {
  const { trackId, ...data } = input;
  const ref = await addDoc(
    collection(db, 'trackComments', trackId, 'comments'),
    {
      trackId,
      userId: data.userId,
      userName: data.userName,
      userImageUrl: data.userImageUrl || '',
      content: data.content,
      timestampSeconds: data.timestampSeconds ?? 0,
      isGlobal: !data.timestampSeconds || data.timestampSeconds <= 0,
      createdAt: Timestamp.now(),
    }
  );
  // The track's owner hears about it in chat (fire-and-forget).
  void import('@/services/track-notices').then(m => m.notifyTrackOwner({
    trackId,
    actor: { userId: data.userId, userName: data.userName, userImage: data.userImageUrl },
    text: `💬 ${String(data.content || '').slice(0, 300)}`,
  })).catch(() => {});
  return ref.id;
}

/**
 * Delete a comment (only the owner or admin should call this).
 */
export async function deleteTrackComment(trackId: string, commentId: string): Promise<void> {
  await deleteDoc(doc(db, 'trackComments', trackId, 'comments', commentId));
}

/**
 * Add a reaction emoji (arrayUnion) to a comment.
 */
export async function addTrackCommentReaction(
  trackId: string,
  commentId: string,
  userId: string,
  emoji: string
): Promise<void> {
  const ref = doc(db, 'trackComments', trackId, 'comments', commentId);
  await updateDoc(ref, {
    [`reactions.${emoji}`]: arrayUnion(userId),
  });
}

/**
 * Remove a reaction emoji (arrayRemove) from a comment.
 */
export async function removeTrackCommentReaction(
  trackId: string,
  commentId: string,
  userId: string,
  emoji: string
): Promise<void> {
  const ref = doc(db, 'trackComments', trackId, 'comments', commentId);
  await updateDoc(ref, {
    [`reactions.${emoji}`]: arrayRemove(userId),
  });
}

/**
 * Subscribe to real-time comments for a track, ordered by timestamp.
 */
export function subscribeTrackComments(
  trackId: string,
  callback: (comments: TrackComment[]) => void
): Unsubscribe {
  const q = query(
    collection(db, 'trackComments', trackId, 'comments'),
    orderBy('timestampSeconds', 'asc')
  );
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as TrackComment))),
    () => callback([])
  );
}
